import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { PwaInstallBanner } from '../components/PwaInstallBanner';
import { pwaConfig } from '../config/pwaConfig';
import { usePwaInstallPrompt } from '../hooks/usePwaInstallPrompt';
import { accountsAPI } from '../services/api';
import { cacheUtils } from '../utils/cache';
import { formatIndianCurrencyWords } from '../utils/numberWords';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  EyeOff,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Coins,
  ExternalLink,
  RotateCcw,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../context/LocalizationContext';
import { saveSelectedAccountContext } from '../utils/accountNavigation';
import { useNotifications } from '../context/NotificationsContext';
import { getUser } from '../services/auth';

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeAccount = (account = {}, isLiability = false) => ({
  ...account,
  acType: String(account.acType || account.accountType || account.ac_type || '').trim(),
  acno: String(
    account.acno ||
      account.acNo ||
      account.accountNumber ||
      account.accountNo ||
      account.account_no ||
      account.ac_no ||
      ''
  ).trim(),
  displayName:
    account.displayName ||
    account.display_name ||
    account.ac_name ||
    account.accountName ||
    account.acType ||
    account.accountType ||
    'Account',
  balance: isLiability ? Math.abs(toNumber(account.balance)) : toNumber(account.balance),
});

export const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, locale, needsCompactFont, t } = useLocalization();
  const { priorityNotifications, markAsRead, refreshNotifications } = useNotifications();
  const user = getUser() || {};
  const {
    shouldShowDashboardInstallBanner,
    shouldShowDashboardIosGuide,
    promptInstall,
    dismissDashboardBanner,
    dismissIosBanner,
  } = usePwaInstallPrompt();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [balanceVisible, setBalanceVisible] = useState(() => {
    try {
      return localStorage.getItem('balance_visible') === '1';
    } catch (_e) {
      return false;
    }
  });
  const [showAmountInWords, setShowAmountInWords] = useState(() => {
    try {
      return localStorage.getItem('show_amount_words') === '1';
    } catch (_e) {
      return false;
    }
  });
  const [activeTab, setActiveTab] = useState(() => {
    const preferredTab = location.state?.dashboardTab;
    return preferredTab === 'iowe' ? 'iowe' : 'iown';
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeTick, setTimeTick] = useState(0);

  const CACHE_KEY = 'dashboard_data';

  useEffect(() => {
    try {
      localStorage.setItem('show_amount_words', showAmountInWords ? '1' : '0');
    } catch (_e) {
      // Ignore storage write errors.
    }
  }, [showAmountInWords]);

  useEffect(() => {
    try {
      localStorage.setItem('balance_visible', balanceVisible ? '1' : '0');
    } catch (_e) {
      // Ignore storage write errors.
    }
  }, [balanceVisible]);

  useEffect(() => {
    if (!lastUpdated) return undefined;
    const timerId = window.setInterval(() => setTimeTick((value) => value + 1), 60000);
    return () => window.clearInterval(timerId);
  }, [lastUpdated]);

  const fetchDashboard = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setErrorMessage('');
    try {
      const response = await accountsAPI.getDashboard();
      // Normalize response: some backends return `data.value` as a JSON string
      const raw = response?.data || {};
      let payload = raw;
      if (raw?.value) {
        if (typeof raw.value === 'object') {
          payload = raw.value;
        } else if (typeof raw.value === 'string') {
          try {
            payload = JSON.parse(raw.value);
          } catch (e) {
            payload = raw.value;
          }
        }
      }

      if (!payload || typeof payload !== 'object') {
        throw new Error(t('dashboard.invalidResponse'));
      }

      // Store in cache and state
      cacheUtils.setCache(CACHE_KEY, payload);
      setDashboardData(payload);
      setLastUpdated(Date.now());
      
      if (!showLoading) {
        toast.success(t('dashboard.refreshSuccess'));
      }
    } catch (error) {
      const message = resolveApiErrorMessage(error, t, t('dashboard.failedToLoad'));
      setErrorMessage(message);
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    const cachedData = cacheUtils.getCache(CACHE_KEY);
    if (cachedData) {
      setDashboardData(cachedData);
      const timestamp = cacheUtils.getCacheTimestamp(CACHE_KEY);
      setLastUpdated(timestamp);
      setLoading(false);
    } else {
      fetchDashboard(true);
    }

    const handleWindowFocus = () => {
      const timestamp = cacheUtils.getCacheTimestamp(CACHE_KEY);
      if (!timestamp) {
        fetchDashboard(true);
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [fetchDashboard]);

  const handleManualRefresh = () => {
    cacheUtils.clearCache(CACHE_KEY);
    fetchDashboard(false);
    void refreshNotifications({ silent: true });
  };

  const getAccountIcon = (acType) => {
    const type = acType?.toUpperCase() || '';
    if (type.includes('SB') || type.includes('SAVINGS')) return PiggyBank;
    if (type.includes('FD') || type.includes('FIXED')) return TrendingUp;
    if (type.includes('RD') || type.includes('RECURRING')) return Coins;
    if (type.includes('CC') || type.includes('LOAN') || type.includes('MORTGAGE')) return CreditCard;
    return Wallet;
  };

  const getAccountTypeAccent = (acType) => {
    const type = acType?.toUpperCase() || '';
    if (type.includes('SB') || type.includes('SAVINGS')) {
      return 'border-l-4 border-l-primary-500';
    }
    if (type.includes('FD') || type.includes('FIXED')) {
      return 'border-l-4 border-l-primary-600';
    }
    if (type.includes('RD') || type.includes('RECURRING')) {
      return 'border-l-4 border-l-primary-400';
    }
    if (type.includes('CC') || type.includes('LOAN') || type.includes('MORTGAGE')) {
      return 'border-l-4 border-l-primary-700';
    }
    return 'border-l-4 border-l-primary-400';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(toNumber(amount));
  };

  const formatAccountNumber = (acno) => {
    if (!acno) return 'N/A';
    const str = acno.toString();
    if (str.length <= 4) return str;
    return str.slice(0, -4).replace(/./g, 'x') + str.slice(-4);
  };

  const openAccountDetails = (account) => {
    const acType = String(account?.acType || '').trim();
    const acno = String(account?.acno || '').trim();
    if (!acType || !acno) {
      toast.error(t('dashboard.incompleteAccount'));
      return;
    }
    const accountContext = {
      acType,
      acno,
      dashboardTab: activeTab,
    };
    saveSelectedAccountContext(accountContext);
    navigate('/account', {
      state: {
        accountContext,
      },
    });
  };

  const openPriorityNotification = (notification) => {
    void markAsRead(notification.id);
    if (notification.actionPath) {
      navigate(notification.actionPath);
      return;
    }
    navigate('/notifications');
  };

  const getPriorityBannerClasses = (severity = 'important') => {
    if (severity === 'critical') {
      return 'border-red-200 bg-red-50/95 text-red-900 dark:border-red-700/70 dark:bg-red-900/45 dark:text-red-50';
    }
    return 'border-primary-200 bg-primary-50/95 text-primary-900 dark:border-primary-700/70 dark:bg-primary-900/45 dark:text-primary-50';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (errorMessage && !dashboardData) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-card-border rounded-xl p-8 text-center" data-testid="dashboard-error-state">
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-3">{t('dashboard.unableToLoad')}</h2>
          <p className="text-text-secondary mb-6">{errorMessage}</p>
          <button
            onClick={() => fetchDashboard(true)}
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-3 hover:bg-primary-600 transition-colors"
            data-testid="retry-dashboard-button"
          >
            <RotateCcw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const iownAccounts = (dashboardData?.iown?.accounts || []).map((item) => normalizeAccount(item, false));
  const ioweAccounts = (dashboardData?.iowe?.accounts || []).map((item) => normalizeAccount(item, true));
  const totalAssets = toNumber(dashboardData?.iown?.total);
  const totalLiabilities = Math.abs(toNumber(dashboardData?.iowe?.total));
  const netWorth = totalAssets - totalLiabilities;
  const whatsNew = (dashboardData?.whats_new || []).filter((item) => item?.enabled !== false);
  const isLiabilityTab = activeTab === 'iowe';
  const activeAccounts = isLiabilityTab ? ioweAccounts : iownAccounts;

  const memberName = String(user.name || '').trim();
  const welcomeHeading = memberName
    ? t('dashboard.pageTitleWithName', { name: memberName })
    : t('dashboard.pageTitle');

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl fade-in" data-testid="dashboard-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-12 -left-10 h-56 w-56 rounded-full bg-primary-200/40 blur-3xl" />
          <div className="absolute top-24 right-6 h-64 w-64 rounded-full bg-primary-200/35 blur-3xl" />
          <div className="absolute bottom-10 left-1/4 h-44 w-44 rounded-full bg-primary-100/45 blur-3xl" />
        </div>
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <h1
              className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}
            >
              {welcomeHeading}
            </h1>
            <p className="app-page-subtitle">{t('dashboard.subtitle')}</p>
            {lastUpdated && (
              <p className="app-page-meta">
                {t('common.lastUpdated', { time: cacheUtils.getTimeAgo(lastUpdated, locale, t) })}
              </p>
            )}
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="app-primary-button whitespace-nowrap"
            title={t('dashboard.refreshDashboard')}
          >
            <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} strokeWidth={2} />
            <span className="text-sm font-medium">{isRefreshing ? t('common.refreshing') : t('common.refresh')}</span>
          </button>
        </div>

        {pwaConfig.dashboardInstallBanner && shouldShowDashboardInstallBanner && (
          <PwaInstallBanner
            mode="install"
            onInstall={promptInstall}
            onDismiss={dismissDashboardBanner}
          />
        )}

        {pwaConfig.iosInstallGuide && shouldShowDashboardIosGuide && (
          <PwaInstallBanner
            mode="ios"
            onDismiss={dismissIosBanner}
          />
        )}

        {priorityNotifications.length > 0 && (
          <div className="space-y-3" data-testid="dashboard-priority-notifications">
            {priorityNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-2xl border px-4 py-4 shadow-sm ${getPriorityBannerClasses(notification.severity)}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-80">
                      {t(`notifications.severity.${notification.severity}`)}
                    </p>
                    <h2 className="mt-1 font-serif text-xl font-semibold">{notification.title}</h2>
                    <p className="mt-2 text-sm leading-6 opacity-90">{notification.message}</p>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] opacity-70">
                      {cacheUtils.getTimeAgo(notification.createdAt, locale)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={() => void markAsRead(notification.id)}
                      className="rounded-full border border-current/20 bg-white/75 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/18"
                    >
                      {t('notifications.markRead')}
                    </button>
                    <button
                      type="button"
                      onClick={() => openPriorityNotification(notification)}
                      className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 dark:bg-white dark:text-accent dark:hover:bg-primary-50"
                    >
                      {notification.actionLabel || t('notifications.actions.openItem')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Net Worth Card */}
        <div className="app-hero-surface p-4 sm:p-5 hover:shadow-xl transition-shadow" data-testid="total-balance-card">
          <div className="flex justify-between items-start mb-5 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-primary-100 text-xs sm:text-sm font-medium mb-2">{t('dashboard.totalNetWorth')}</p>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl font-bold">
                  {balanceVisible ? formatCurrency(netWorth) : '₹ •••••••'}
                </h2>
                <button
                  onClick={() => {
                    const nextVisible = !balanceVisible;
                    setBalanceVisible(nextVisible);
                    try {
                      localStorage.setItem('balance_visible', nextVisible ? '1' : '0');
                    } catch (_e) {
                      // Ignore storage write errors.
                    }
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
                  data-testid="toggle-balance-visibility"
                  title={t('dashboard.toggleBalanceVisibility')}
                >
                  {balanceVisible ? <EyeOff size={18} className="sm:w-6 sm:h-6" /> : <Eye size={18} className="sm:w-6 sm:h-6" />}
                </button>
                {balanceVisible && (
                  <button
                    type="button"
                    onClick={() => setShowAmountInWords((prev) => !prev)}
                    className={`px-2.5 py-1 text-xs sm:text-sm rounded-md border transition-colors ${
                      showAmountInWords
                        ? 'bg-white/25 border-white/40 text-white'
                        : 'bg-white/10 border-white/30 text-white/90 hover:bg-white/20'
                    }`}
                    data-testid="toggle-amount-words"
                    title={t('dashboard.toggleAmountWords')}
                  >
                    {showAmountInWords ? t('common.wordsOn') : t('common.words')}
                  </button>
                )}
              </div>
              {showAmountInWords && balanceVisible && (
                <p className="mt-2 text-xs sm:text-sm text-primary-100/95 leading-relaxed font-sans font-medium">
                  {formatIndianCurrencyWords(netWorth, language)}
                </p>
              )}
            </div>
            <div className="bg-white/20 backdrop-blur-md p-2.5 sm:p-3 rounded-xl border border-white/30 flex-shrink-0">
              <Wallet size={28} strokeWidth={1.5} className="text-white sm:w-8 sm:h-8" />
            </div>
          </div>

          {/* Tabs Section */}
          <div className="border-t border-white/30 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 items-stretch">
              {/* I Own Tab */}
              <button
                type="button"
                onClick={() => setActiveTab('iown')}
                className={`group relative p-2.5 sm:p-3 min-h-[88px] sm:min-h-[96px] rounded-xl backdrop-blur-md transition-all ${
                  activeTab === 'iown'
                    ? 'bg-white/30 border border-white/50 shadow-lg'
                    : 'bg-white/10 border border-white/20 hover:bg-white/20'
                }`}
              >
                <div className="flex items-start sm:items-center gap-2">
                  <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                    <ArrowDownRight size={18} className="sm:w-5 sm:h-5" strokeWidth={2} />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-white/80 text-xs font-medium mb-0.5 truncate">{t('dashboard.iOwn')}</p>
                    <p className={`font-serif text-sm sm:text-lg md:text-xl font-bold truncate leading-tight ${activeTab === 'iown' ? 'text-white' : 'text-white/90'}`}>
                      {balanceVisible ? formatCurrency(totalAssets) : '₹ •••'}
                    </p>
                  </div>
                </div>
              </button>

              {/* I Owe Tab */}
              <button
                type="button"
                onClick={() => setActiveTab('iowe')}
                className={`group relative p-2.5 sm:p-3 min-h-[88px] sm:min-h-[96px] rounded-xl backdrop-blur-md transition-all ${
                  activeTab === 'iowe'
                    ? 'bg-white/30 border border-white/50 shadow-lg'
                    : 'bg-white/10 border border-white/20 hover:bg-white/20'
                }`}
              >
                <div className="flex items-start sm:items-center gap-2">
                  <div className="bg-white/20 p-2 rounded-lg flex-shrink-0">
                    <ArrowUpRight size={18} className="sm:w-5 sm:h-5" strokeWidth={2} />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-white/80 text-xs font-medium mb-0.5 truncate">{t('dashboard.iOwe')}</p>
                    <p className={`font-serif text-sm sm:text-lg md:text-xl font-bold truncate leading-tight ${activeTab === 'iowe' ? 'text-white' : 'text-white/90'}`}>
                      {balanceVisible ? formatCurrency(totalLiabilities) : '₹ •••••'}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {!!errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 text-sm">
            <p className="text-red-800 dark:text-red-200 mb-1.5">{t('dashboard.cachedInfoMessage')}</p>
            <button 
              onClick={fetchDashboard} 
              className="text-red-700 dark:text-red-300 font-medium hover:underline inline-flex items-center gap-2"
            >
              <RotateCcw size={16} /> {t('common.retry')}
            </button>
          </div>
        )}

        {/* Accounts Section */}
        <div className="mt-1">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-primary-900 dark:text-white">
              {isLiabilityTab ? t('dashboard.liabilitiesAndLoans') : t('dashboard.assetsAndInvestments')}
            </h2>
            <p className="text-sm sm:text-base font-medium text-primary-800 dark:text-primary-200 bg-primary-50 dark:bg-slate-800 border border-primary-200 dark:border-slate-600 px-4 py-2 rounded-full shrink-0" data-testid="account-count">
              {t('dashboard.activeCount', { count: activeAccounts.length })}
            </p>
          </div>

          {activeAccounts.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-800 rounded-xl p-10 text-center" data-testid="no-accounts-message">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-primary-300 dark:text-primary-600" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl font-semibold text-primary-900 dark:text-white mb-2">{t('dashboard.noAccountsFound')}</h3>
              <p className="text-primary-700 dark:text-primary-300">{t('dashboard.contactBranch')}</p>
            </div>
          ) : (
            <div key={`account-grid-${activeTab}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch" data-testid={`account-grid-${activeTab}`}>
              {activeAccounts.map((account, index) => {
                const IconComponent = getAccountIcon(account.acType);
                const isLiability = isLiabilityTab;
                const typeAccent = getAccountTypeAccent(account.acType);
                return (
                  <div
                    key={`${activeTab}-${account.acType || 'na'}-${account.acno || 'na'}-${index}`}
                    onClick={() => openAccountDetails(account)}
                    className={`group h-full bg-white dark:bg-slate-900 rounded-xl border border-primary-200 dark:border-slate-800 ${typeAccent} p-3.5 cursor-pointer hover:shadow-lg hover:border-primary-400 dark:hover:border-primary-500 transition-all`}
                    data-testid={`account-card-${activeTab}-${account.acno || index}`}
                  >
                    {/* Card Header */}
                    <div className="flex justify-between items-start mb-3.5">
                      <div className={`p-2.5 rounded-xl group-hover:scale-105 transition-transform ${
                        isLiability 
                          ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20' 
                          : 'bg-primary-50 dark:bg-primary-900/20'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${
                          isLiability ? 'text-red-600 dark:text-red-400' : 'text-primary-600 dark:text-primary-400'
                        }`} strokeWidth={1.5} />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-white bg-primary-500 dark:bg-primary-600 px-3 py-1 rounded-full">
                        {t('common.active')}
                      </span>
                    </div>

                    {/* Card Content */}
                    <div className="mb-3.5">
                      <h3 className="font-semibold text-primary-900 dark:text-white mb-1 text-base">{account.displayName || account.acType}</h3>
                      <p className="text-sm text-primary-600 dark:text-primary-300">A/C {formatAccountNumber(account.acno)}</p>
                    </div>

                    {/* Card Footer */}
                    <div className="pt-3.5 border-t border-primary-200 dark:border-slate-700">
                      <p className="text-xs font-medium text-primary-700 dark:text-primary-300 uppercase tracking-wide mb-1.5">
                        {isLiability ? t('dashboard.outstandingAmount') : t('common.availableBalance')}
                      </p>
                      <p className={`font-serif text-[1.4rem] font-bold ${
                        isLiability ? 'text-red-600 dark:text-red-400' : 'text-primary-600 dark:text-primary-400'
                      }`}>
                        {balanceVisible ? (isLiability ? formatCurrency(Math.abs(account.balance)) : formatCurrency(account.balance)) : '₹ •••••'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {whatsNew.length > 0 && (
          <div className="mt-6">
            <div className="mb-4">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-primary-900 dark:text-white">{t('dashboard.whatsNew')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {whatsNew.map((item, index) => (
                <div
                  key={item.id || index}
                  onClick={() => item.href && window.open(item.href, '_blank', 'noopener,noreferrer')}
                  className={`group bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-primary-200 dark:border-slate-800 hover:shadow-lg hover:border-primary-400 dark:hover:border-primary-500 transition-all cursor-pointer ${item.href ? 'hover:scale-[1.01]' : ''}`}
                  data-testid={`whats-new-${index}`}
                >
                  {item.banner_image && (
                    <img
                      src={item.banner_image}
                      alt={item.banner_tagline}
                      className="w-full h-36 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="p-4">
                    <h3 className="font-serif text-lg font-semibold text-primary-900 dark:text-white mb-1.5 flex items-center justify-between">
                      {item.banner_tagline}
                      {item.href && <ExternalLink size={16} className="text-primary-500 group-hover:translate-x-1 transition-transform" />}
                    </h3>
                    {item.banner_content && (
                      <p className="text-primary-700 dark:text-primary-300 text-sm">{item.banner_content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
